import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';

const EARTH_RADIUS_KM = 6371;

/**
 * Drives the Find in store drawer: keeps the store list in step with the
 * selected variant, and sorts it by distance once the shopper tells us where
 * they are.
 *
 * Stock states arrive pre-rendered from sections/store-availability.liquid —
 * the raw per-store quantities are read server-side and never sent here.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} list - Wrapper the fetched store list is morphed into.
 * @property {HTMLInputElement} [locationInput] - Suburb / postcode field.
 * @property {HTMLFormElement} [locationForm] - Form wrapping the field.
 * @property {HTMLButtonElement} [locateButton] - "Use my current location".
 * @property {HTMLElement} [status] - Live region for search feedback.
 * @property {HTMLElement} [colourName] - Heading echoing the selected colour's name.
 *
 * @extends Component<Refs>
 */
class FindInStore extends Component {
  requiredRefs = ['list'];

  /** @type {AbortController | undefined} */
  #activeFetch;

  /** @type {{lat: number, lng: number} | null} */
  #origin = null;

  /** @type {Promise<any> | null} */
  #postcodes = null;

  /** @type {EventTarget | null} */
  #scope = null;

  connectedCallback() {
    super.connectedCallback();

    // The variant picker sits in the same section but outside this dialog, so
    // its event is caught at the section rather than locally.
    this.#scope = this.closest('.shopify-section') ?? document;
    this.#scope.addEventListener(ThemeEvents.variantUpdate, this.#variantUpdated);

    this.refs.locationForm?.addEventListener('submit', this.#submitSearch);
    this.refs.locateButton?.addEventListener('click', this.#useCurrentLocation);
    this.addEventListener('click', this.#proxyOptionClick);

    this.#syncOptionState();

    // A combined-listing colour change morphs <main>, tearing this element
    // down mid-interaction. #proxyOptionClick flags it beforehand so the
    // drawer comes back rather than vanishing under the shopper.
    if (document.documentElement.hasAttribute('data-find-in-store-reopen')) {
      document.documentElement.removeAttribute('data-find-in-store-reopen');
      /** @type {any} */ (this.closest('dialog-component'))?.showDialog();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scope?.removeEventListener(ThemeEvents.variantUpdate, this.#variantUpdated);
    this.#activeFetch?.abort();
  }

  /**
   * Mirrors the drawer's own colour / size controls onto the real variant
   * picker by clicking its input, so the PDP stays the single source of truth
   * for what's selected. The resulting variant:update brings the list back.
   *
   * @param {MouseEvent} event
   */
  #proxyOptionClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-option-value-id]') : null;
    if (!target) return;

    const picker = this.#scope instanceof Element ? this.#scope.querySelector('variant-picker') : null;
    const input = picker?.querySelector(
      `input[data-option-value-id="${CSS.escape(target.getAttribute('data-option-value-id') ?? '')}"]`
    );
    if (!(input instanceof HTMLInputElement) || input.checked) return;

    // A colour that belongs to a different product reloads the PDP wholesale.
    const connected = input.dataset.connectedProductUrl;
    const current = picker instanceof HTMLElement ? picker.dataset.productUrl?.split('?')[0] : undefined;
    if (connected && connected !== current) {
      document.documentElement.setAttribute('data-find-in-store-reopen', '');
    }

    this.#paintSelection(target);
    input.click();
  };

  /**
   * Paints the clicked proxy as selected immediately, ahead of the variant
   * round-trip that #syncOptionState waits on.
   *
   * The real picker still OWNS selection — this only borrows the paint for the
   * ~1s a section render takes on this store. Without it the button kept its
   * unselected look until variant:update landed, which read as the tap having
   * been ignored (the same reason quick-add confirms before the cart responds,
   * see assets/quick-add-sizes.js). #syncOptionState rewrites aria-pressed on
   * every proxy when the update arrives, so a guess that turns out wrong is
   * corrected rather than left standing.
   *
   * Scoped to the clicked button's own group, so choosing a size cannot clear
   * the painted colour or vice versa.
   *
   * @param {Element} target
   */
  #paintSelection(target) {
    const group = target.closest('.find-in-store__sizes, .find-in-store__colours');
    if (!group) return;

    for (const button of group.querySelectorAll('[data-option-value-id]')) {
      button.setAttribute('aria-pressed', String(button === target));
    }

    // Colour proxies carry a name; echo it the way #syncOptionState does so the
    // heading keeps pace with the swatch.
    const name = /** @type {HTMLElement} */ (target).dataset.optionName;
    if (name && this.refs.colourName) this.refs.colourName.textContent = name;
  }

  /** @param {any} event */
  #variantUpdated = (event) => {
    // Clear the reopen flag even when the morph preserved this element, so a
    // stale one can't pop the drawer open later.
    document.documentElement.removeAttribute('data-find-in-store-reopen');

    const { resource, data } = event.detail;
    if (data?.newProduct) this.dataset.productUrl = data.newProduct.url;

    const variantId = resource ? String(resource.id) : null;
    if (!variantId || variantId === this.dataset.variantId) return;

    this.dataset.variantId = variantId;
    this.#syncOptionState();
    this.#fetchAvailability(variantId);
  };

  /** @param {string} variantId */
  #fetchAvailability = (variantId) => {
    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    this.refs.list.setAttribute('aria-busy', 'true');

    fetch(`${this.dataset.productUrl}?variant=${variantId}&section_id=store-availability`, {
      signal: abortController.signal,
    })
      .then((response) => response.text())
      .then((text) => {
        if (abortController.signal.aborted) return;

        const html = new DOMParser().parseFromString(text, 'text/html');
        const updated = html.querySelector(`[data-store-availability][data-variant-id="${variantId}"]`);
        const current = this.refs.list.querySelector('[data-store-availability]');

        if (updated && current) morph(current, updated);

        this.refs.list.removeAttribute('aria-busy');
        // Distances live on the old nodes; recompute against the new ones.
        this.#applyDistances();
      })
      .catch(() => {
        if (abortController.signal.aborted) return;
        this.refs.list.removeAttribute('aria-busy');
      });
  };

  /** @param {SubmitEvent} event */
  #submitSearch = (event) => {
    event.preventDefault();

    const query = this.refs.locationInput?.value.trim() ?? '';
    if (!query) return;

    this.#resolveQuery(query)
      .then((place) => {
        if (!place) {
          this.#setStatus(this.dataset.noResultsText ?? '');
          return;
        }
        this.#origin = place;
        this.#setStatus('');
        this.#applyDistances();
      })
      .catch(() => this.#setStatus(this.dataset.noResultsText ?? ''));
  };

  #useCurrentLocation = () => {
    if (!navigator.geolocation) {
      this.#setStatus(this.dataset.locationErrorText ?? '');
      return;
    }

    this.refs.locateButton?.setAttribute('aria-busy', 'true');
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        this.refs.locateButton?.removeAttribute('aria-busy');
        this.#origin = { lat: coords.latitude, lng: coords.longitude };
        this.#setStatus('');
        this.#applyDistances();
      },
      () => {
        this.refs.locateButton?.removeAttribute('aria-busy');
        this.#setStatus(this.dataset.locationErrorText ?? '');
      },
      { timeout: 10000, maximumAge: 300000 }
    );
  };

  /**
   * Resolves a suburb name or 4-digit postcode to a centroid. The lookup table
   * is only fetched once a shopper actually searches.
   *
   * @param {string} query
   * @returns {Promise<{lat: number, lng: number} | null>}
   */
  async #resolveQuery(query) {
    const url = this.dataset.postcodesUrl;
    if (!url) return null;

    this.#postcodes ??= fetch(url).then((response) => response.json());
    const data = await this.#postcodes;

    const term = query.toLowerCase().replace(/\s+/g, ' ').trim();
    const postcode = /^\d{4}$/.test(term) ? term : data.s?.[term];
    const entry = postcode ? data.p?.[postcode] : null;

    return entry ? { lat: entry[0], lng: entry[1] } : null;
  }

  /**
   * Writes a distance onto every store that has coordinates, then reorders the
   * list nearest-first. Stores without coordinates keep their alphabetical
   * position at the end.
   */
  #applyDistances() {
    const list = this.refs.list.querySelector('.store-availability__stores');
    if (!(list instanceof HTMLElement)) return;

    const origin = this.#origin;
    const items = /** @type {HTMLElement[]} */ ([...list.children]);

    for (const item of items) {
      const label = item.querySelector('[data-store-distance]');
      const lat = Number(item.dataset.lat);
      const lng = Number(item.dataset.lng);

      if (!origin || !item.dataset.lat || Number.isNaN(lat) || Number.isNaN(lng)) {
        label?.setAttribute('hidden', '');
        continue;
      }

      const distance = haversine(origin.lat, origin.lng, lat, lng);
      item.dataset.distance = String(distance);
      if (label) {
        label.textContent = formatDistance(distance);
        label.removeAttribute('hidden');
      }
    }

    if (!origin) return;

    items
      .slice()
      .sort((a, b) => Number(a.dataset.distance ?? Infinity) - Number(b.dataset.distance ?? Infinity))
      .forEach((item) => list.appendChild(item));
  }

  /**
   * Reflects the real picker's selection onto the drawer's proxy controls. The
   * picker morphs itself on every variant change, so its checked inputs are the
   * authority — the drawer never tracks selection of its own.
   */
  #syncOptionState() {
    const picker = this.#scope instanceof Element ? this.#scope.querySelector('variant-picker') : null;
    if (!picker) return;

    const selected = new Set(
      [...picker.querySelectorAll('fieldset input:checked')].map(
        (input) => /** @type {HTMLInputElement} */ (input).dataset.optionValueId
      )
    );

    for (const button of this.querySelectorAll('[data-option-value-id]')) {
      const isSelected = selected.has(button.getAttribute('data-option-value-id') ?? '');
      button.setAttribute('aria-pressed', String(isSelected));

      // Only the colour proxies carry a name; the heading echoes the selected
      // one the way the PDP swatch legend does.
      const name = /** @type {HTMLElement} */ (button).dataset.optionName;
      if (isSelected && name && this.refs.colourName) {
        this.refs.colourName.textContent = name;
      }
    }
  }

  /** @param {string} message */
  #setStatus(message) {
    if (this.refs.status) this.refs.status.textContent = message;
  }
}

/**
 * Great-circle distance in kilometres.
 *
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @returns {number}
 */
function haversine(lat1, lng1, lat2, lng2) {
  const toRad = (value) => (value * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;

  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a));
}

/**
 * @param {number} km
 * @returns {string}
 */
function formatDistance(km) {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km < 10 ? km.toFixed(1) : Math.round(km)} km`;
}

if (!customElements.get('find-in-store')) {
  customElements.define('find-in-store', FindInStore);
}
